import type {
  AdminAuditEvent,
  AdminAuditSeverity,
} from "@/types/admin-audit";
import type { MockUserId } from "@/lib/api/mock-data/users.mock";

interface EventOpts {
  reason?: string | null;
  severity?: AdminAuditSeverity;
  diff?: { before?: unknown; after?: unknown };
}

const event = (
  eventId: string,
  action: AdminAuditEvent["action"],
  targetType: AdminAuditEvent["targetType"],
  targetId: string,
  occurredAt: string,
  opts: EventOpts = {},
): AdminAuditEvent => ({
  eventId,
  action,
  actorAdminId: "adm_root",
  actorDisplayName: "Root Admin",
  targetType,
  targetId,
  reason: opts.reason ?? null,
  ipAddress: "10.0.0.1",
  userAgent: "ZoneAdmin/1.0",
  occurredAt,
  severity: opts.severity ?? "info",
  requestId: `req_${eventId}`,
  ...(opts.diff !== undefined ? { diff: opts.diff } : {}),
});

export const MOCK_AUDIT_EVENTS_BY_USER: Record<
  MockUserId,
  AdminAuditEvent[]
> = {
  u_001_alice_active: [
    event(
      "ae_001_a",
      "user.viewed",
      "user",
      "u_001_alice_active",
      "2026-05-20T09:00:00Z",
    ),
  ],
  u_002_bob_trial_pending: [
    event(
      "ae_002_a",
      "user.viewed",
      "user",
      "u_002_bob_trial_pending",
      "2026-05-15T11:00:00Z",
    ),
  ],
  u_003_cara_trial_ending: [
    event(
      "ae_003_a",
      "membership.trial_banner_sent",
      "membership",
      "m_003",
      "2026-05-19T09:00:00Z",
      { reason: "D21 trial-ending banner triggered." },
    ),
  ],
  u_004_dave_card_failed: [
    event(
      "ae_004_a",
      "payment.card_failed",
      "membership",
      "m_004",
      "2026-05-13T11:15:00Z",
      { reason: "insufficient_funds", severity: "warn" },
    ),
  ],
  u_005_erin_charged_off: [
    event(
      "ae_005_a",
      "membership.charge_off",
      "membership",
      "m_005",
      "2026-04-02T13:20:00Z",
      {
        reason: "Trial deadline passed; device not returned.",
        severity: "crit",
        // Dangerous action with a redactable sensitive value in `before` —
        // exercised by audit redaction tests.
        diff: {
          before: {
            state: "trial_active",
            paymentMethodLast4: "2244",
            stripeCustomerId: "cus_secret_3xKQz9R",
          },
          after: { state: "trial_charged_off", chargedOffAmountMinor: 84_000 },
        },
      },
    ),
    event(
      "ae_005_b",
      "reclaim.charged_off",
      "reclaim",
      "rma_005",
      "2026-04-02T13:21:00Z",
      { severity: "crit" },
    ),
  ],
  u_006_finn_past_due: [
    event(
      "ae_006_a",
      "payment.renewal_failed",
      "membership",
      "m_006",
      "2026-05-01T08:00:00Z",
      { severity: "warn" },
    ),
    event(
      "ae_006_b",
      "dispute.opened",
      "dispute",
      "dp_001",
      "2026-05-12T11:30:00Z",
      { severity: "warn" },
    ),
  ],
  u_007_gina_suspended: [
    event(
      "ae_007_a",
      "fraud.signal_triggered",
      "user",
      "u_007_gina_suspended",
      "2026-03-12T09:00:00Z",
      { reason: "household_repeat", severity: "warn" },
    ),
    event(
      "ae_007_b",
      "membership.suspended",
      "membership",
      "m_007",
      "2026-03-14T10:00:00Z",
      {
        reason: "Manual suspension by support after fraud review.",
        severity: "crit",
        diff: {
          before: { state: "active" },
          after: {
            state: "suspended",
            suspendedReason: "fraud_household_repeat",
          },
        },
      },
    ),
    event(
      "ae_007_c",
      "device.blacklisted",
      "device",
      "ZB1-G007-44CB",
      "2026-03-14T10:01:00Z",
      {
        severity: "crit",
        diff: {
          before: { blacklisted: false, status: "lost" },
          after: { blacklisted: true, status: "lost" },
        },
      },
    ),
  ],
  u_008_hugo_cancelled: [
    event(
      "ae_008_a",
      "membership.cancelled",
      "membership",
      "m_008",
      "2026-05-10T18:30:00Z",
      {
        reason: "User-initiated cancel; runs until term end.",
        severity: "warn",
      },
    ),
    event(
      "ae_008_b",
      "user.deletion_requested",
      "user",
      "u_008_hugo_cancelled",
      "2026-05-10T18:35:00Z",
      { severity: "warn" },
    ),
  ],
};

export const MOCK_AUDIT_EVENTS_BY_ID: Record<string, AdminAuditEvent> =
  Object.fromEntries(
    Object.values(MOCK_AUDIT_EVENTS_BY_USER)
      .flat()
      .map((e) => [e.eventId, e]),
  );

export const MOCK_AUDIT_EVENTS_ALL: AdminAuditEvent[] = Object.values(
  MOCK_AUDIT_EVENTS_BY_USER,
).flat();
